import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AOS from 'aos';
import { FaPlus, FaMinus, FaPhone, FaWhatsapp, FaCheckCircle, FaSearch } from 'react-icons/fa';
import SEO from '../components/SEO';
import { faqData, faqCategories } from '../data/faqData';
import './FAQ.css';

const FAQ = () => {
  const [activeCategory, setActiveCategory] = useState('All');
  const [openFaq, setOpenFaq] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredFaqs, setFilteredFaqs] = useState(faqData);

  useEffect(() => { AOS.init({ duration: 1000, once: true }); }, []);

  useEffect(() => {
    let result = faqData;
    if (activeCategory !== 'All') result = result.filter(f => f.category === activeCategory);
    if (searchQuery.trim()) result = result.filter(f =>
      f.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      f.answer.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredFaqs(result);
  }, [activeCategory, searchQuery]);

  const toggleFaq = (id) => setOpenFaq(openFaq === id ? null : id);

  return (
    <>
      <SEO
        title="FAQ - Frequently Asked Questions | SRI KRISH CIVIL SUPPLIER Coimbatore"
        description="Find answers to common questions about building demolition, earthwork, equipment rental, and building materials services in Coimbatore. SRI KRISH CIVIL SUPPLIER FAQ."
        keywords="construction faq coimbatore, demolition questions, earthwork faq, equipment rental faq, m sand faq, jcb rent faq coimbatore, civil supplier faq"
      />

      <div className="page-header faq-header">
        <div className="container">
          <h1 data-aos="fade-up">Frequently Asked Questions</h1>
          <p data-aos="fade-up" data-aos-delay="100">Everything You Need to Know About Our Services in Coimbatore</p>
        </div>
      </div>

      {/* FAQ Stats */}
      <section className="faq-stats-section">
        <div className="container">
          <div className="faq-stats-grid">
            <div className="faq-stat" data-aos="fade-up">
              <strong>{faqData.length}+</strong>
              <span>Questions Answered</span>
            </div>
            <div className="faq-stat" data-aos="fade-up" data-aos-delay="100">
              <strong>2 hrs</strong>
              <span>Response Time</span>
            </div>
            <div className="faq-stat" data-aos="fade-up" data-aos-delay="200">
              <strong>300+</strong>
              <span>Happy Clients</span>
            </div>
            <div className="faq-stat" data-aos="fade-up" data-aos-delay="300">
              <strong>15+</strong>
              <span>Years Experience</span>
            </div>
          </div>
        </div>
      </section>

      <section className="faq-section">
        <div className="container">

          {/* Search */}
          <div className="faq-search" data-aos="fade-up">
            <div className="search-input-wrapper">
              <FaSearch />
              <input
                type="text"
                placeholder="Search questions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          {/* Categories */}
          <div className="faq-categories" data-aos="fade-up">
            {faqCategories.map(category => (
              <button
                key={category}
                className={`category-btn ${activeCategory === category ? 'active' : ''}`}
                onClick={() => setActiveCategory(category)}
              >
                {category}
              </button>
            ))}
          </div>

          {/* FAQ Count */}
          <p className="faq-count" data-aos="fade-up">
            Showing {filteredFaqs.length} question{filteredFaqs.length !== 1 ? 's' : ''}
            {activeCategory !== 'All' ? ` in "${activeCategory}"` : ''}
            {searchQuery ? ` matching "${searchQuery}"` : ''}
          </p>

          {/* FAQ List */}
          <div className="faq-list">
            {filteredFaqs.length === 0 ? (
              <div className="faq-empty">
                <p>No questions found. Try a different search or category.</p>
                <a href="tel:9894065756" className="btn btn-primary"><FaPhone /> Call Us Directly</a>
              </div>
            ) : (
              filteredFaqs.map((faq, index) => (
                <div
                  key={faq.id}
                  className={`faq-item ${openFaq === faq.id ? 'active' : ''}`}
                  data-aos="fade-up"
                  data-aos-delay={index * 30}
                >
                  <div className="faq-question" onClick={() => toggleFaq(faq.id)}>
                    <div className="faq-question-left">
                      <span className="faq-category-tag">{faq.category}</span>
                      <h3>{faq.question}</h3>
                    </div>
                    <span className="faq-icon">{openFaq === faq.id ? <FaMinus /> : <FaPlus />}</span>
                  </div>
                  {openFaq === faq.id && (
                    <div className="faq-answer">
                      <p>{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Still Have Questions */}
      <section className="faq-contact-section">
        <div className="container">
          <div className="faq-contact-content" data-aos="zoom-in">
            <h2>Still Have Questions?</h2>
            <p>Can't find the answer you're looking for? Our team is ready to help. Call or WhatsApp us and get an answer within minutes.</p>
            <div className="faq-contact-grid">
              <div className="faq-contact-card">
                <FaPhone />
                <h3>Call Us</h3>
                <p>Speak directly with our experts</p>
                <a href="tel:9894065756" className="btn btn-primary">9894065756</a>
              </div>
              <div className="faq-contact-card">
                <FaWhatsapp />
                <h3>WhatsApp</h3>
                <p>Send us your question anytime</p>
                <a href="https://wa.me/919894065756" className="btn btn-whatsapp" target="_blank" rel="noopener noreferrer">Chat Now</a>
              </div>
              <div className="faq-contact-card">
                <FaCheckCircle />
                <h3>Free Quote</h3>
                <p>Get a detailed quote for your project</p>
                <Link to="/contact" className="btn btn-secondary">Contact Form</Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default FAQ;
